import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, CheckCircle2, XCircle, FileJson, Lock } from "lucide-react";
import { LoginButton } from "./LoginButton";
import { useAuth } from "@/lib/store/auth-store";

export function UploadForm() {
  const [unitsFile, setUnitsFile] = useState<File | null>(null);
  const [weaponsFile, setWeaponsFile] = useState<File | null>(null);
  const [divisionsFile, setDivisionsFile] = useState<File | null>(null);
  const [damageTableFile, setDamageTableFile] = useState<File | null>(null);
  const [unitTraitEffectsFile, setUnitTraitEffectsFile] = useState<File | null>(null);
  const [terrainSpeedModifiersFile, setTerrainSpeedModifiersFile] = useState<File | null>(null);
  const [version, setVersion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [multiFileFeedback, setMultiFileFeedback] = useState<string | null>(null);
  const { user, loading: checkingAuth } = useAuth();

  const validateVersion = (v: string): boolean => {
    return /^v\d+\.\d+\.\d+$/.test(v);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!unitsFile) {
      setError("Units JSON file is required");
      return;
    }

    if (!weaponsFile) {
      setError("Weapons JSON file is required");
      return;
    }

    if (!divisionsFile) {
      setError("Divisions JSON file is required");
      return;
    }

    if (!damageTableFile) {
      setError("Damage Table JSON file is required");
      return;
    }

    if (!unitTraitEffectsFile) {
      setError("Unit Trait Effects JSON file is required");
      return;
    }

    if (!terrainSpeedModifiersFile) {
      setError("Terrain Speed Modifiers JSON file is required");
      return;
    }

    if (!version) {
      setError("Version number is required");
      return;
    }

    if (!validateVersion(version)) {
      setError("Version must be in format v1.0.0 (e.g., v1.0.0, v1.1.0)");
      return;
    }

    setLoading(true);

    try {
      // Check if version already exists for all file types
      const versionChecks = [
        fetch("/api/units/versions"),
        fetch("/api/weapons/versions"),
        fetch("/api/divisions/versions"),
        fetch("/api/damage-table/versions"),
        fetch("/api/unit-trait-effects/versions"),
        fetch("/api/terrain-speed-modifiers/versions"),
      ];

      const [unitsVersionsRes, weaponsVersionsRes, divisionsVersionsRes, damageTableVersionsRes, unitTraitEffectsVersionsRes, terrainSpeedModifiersVersionsRes] = await Promise.all(versionChecks);

      let unitsVersionExists = false;
      let weaponsVersionExists = false;
      let divisionsVersionExists = false;
      let damageTableVersionExists = false;
      let unitTraitEffectsVersionExists = false;
      let terrainSpeedModifiersVersionExists = false;

      if (unitsVersionsRes && unitsVersionsRes.ok) {
        const unitsVersions = await unitsVersionsRes.json();
        unitsVersionExists = unitsVersions.some((v: { version: string }) => v.version === version);
      }

      if (weaponsVersionsRes && weaponsVersionsRes.ok) {
        const weaponsVersions = await weaponsVersionsRes.json();
        weaponsVersionExists = weaponsVersions.some((v: { version: string }) => v.version === version);
      }

      if (divisionsVersionsRes && divisionsVersionsRes.ok) {
        const divisionsVersions = await divisionsVersionsRes.json();
        divisionsVersionExists = divisionsVersions.some((v: { version: string }) => v.version === version);
      }

      if (damageTableVersionsRes && damageTableVersionsRes.ok) {
        const damageTableVersions = await damageTableVersionsRes.json();
        damageTableVersionExists = damageTableVersions.some((v: { version: string }) => v.version === version);
      }

      if (unitTraitEffectsVersionsRes && unitTraitEffectsVersionsRes.ok) {
        const unitTraitEffectsVersions = await unitTraitEffectsVersionsRes.json();
        unitTraitEffectsVersionExists = unitTraitEffectsVersions.some((v: { version: string }) => v.version === version);
      }

      if (terrainSpeedModifiersVersionsRes && terrainSpeedModifiersVersionsRes.ok) {
        const terrainSpeedModifiersVersions = await terrainSpeedModifiersVersionsRes.json();
        terrainSpeedModifiersVersionExists = terrainSpeedModifiersVersions.some((v: { version: string }) => v.version === version);
      }

      // Read and parse files
      const fileReads = [
        unitsFile.text(),
        weaponsFile.text(),
        divisionsFile.text(),
        damageTableFile.text(),
        unitTraitEffectsFile.text(),
        terrainSpeedModifiersFile.text(),
      ];

      const [unitsText, weaponsText, divisionsText, damageTableText, unitTraitEffectsText, terrainSpeedModifiersText] = await Promise.all(fileReads);

      if (!unitsText || !weaponsText || !divisionsText || !damageTableText || !unitTraitEffectsText || !terrainSpeedModifiersText) {
        throw new Error("Failed to read required files");
      }

      const unitsData = JSON.parse(unitsText);
      const weaponsData = JSON.parse(weaponsText);
      const divisionsData = JSON.parse(divisionsText);
      const damageTableData = JSON.parse(damageTableText);
      const unitTraitEffectsData = JSON.parse(unitTraitEffectsText);
      const terrainSpeedModifiersData = JSON.parse(terrainSpeedModifiersText);

      // Build URLs with replace=true if version exists
      const unitsUrl = unitsVersionExists ? `/api/units?replace=true` : "/api/units";
      const weaponsUrl = weaponsVersionExists ? `/api/weapons?replace=true` : "/api/weapons";
      const divisionsUrl = divisionsVersionExists ? `/api/divisions?replace=true` : "/api/divisions";
      const damageTableUrl = damageTableVersionExists ? `/api/damage-table?replace=true` : "/api/damage-table";
      const unitTraitEffectsUrl = unitTraitEffectsVersionExists ? `/api/unit-trait-effects?replace=true` : "/api/unit-trait-effects";
      const terrainSpeedModifiersUrl = terrainSpeedModifiersVersionExists ? `/api/terrain-speed-modifiers?replace=true` : "/api/terrain-speed-modifiers";

      // Upload files
      const uploadPromises = [
        fetch(unitsUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            version,
            data: unitsData,
          }),
        }),
        fetch(weaponsUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            version,
            data: weaponsData,
          }),
        }),
          fetch(divisionsUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              version,
              data: divisionsData,
            }),
        }),
        fetch(damageTableUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            version,
            data: damageTableData,
          }),
        }),
        fetch(unitTraitEffectsUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            version,
            data: unitTraitEffectsData,
          }),
        }),
        fetch(terrainSpeedModifiersUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            version,
            data: terrainSpeedModifiersData,
          }),
        }),
      ];

      const responses = await Promise.all(uploadPromises);
      const unitsRes = responses[0];
      const weaponsRes = responses[1];
      const divisionsRes = responses[2];
      const damageTableRes = responses[3];
      const unitTraitEffectsRes = responses[4];
      const terrainSpeedModifiersRes = responses[5];

      if (!unitsRes || !unitsRes.ok) {
        const unitsError = unitsRes ? await unitsRes.json() : { error: "No response" };
        throw new Error(`Units upload failed: ${unitsError.error || unitsRes?.statusText || "Unknown error"}`);
      }

      if (!weaponsRes || !weaponsRes.ok) {
        const weaponsError = weaponsRes ? await weaponsRes.json() : { error: "No response" };
        throw new Error(`Weapons upload failed: ${weaponsError.error || weaponsRes?.statusText || "Unknown error"}`);
      }

      if (!divisionsRes || !divisionsRes.ok) {
        const divisionsError = divisionsRes ? await divisionsRes.json() : { error: "No response" };
        throw new Error(`Divisions upload failed: ${divisionsError.error || divisionsRes?.statusText || "Unknown error"}`);
      }

      if (!damageTableRes || !damageTableRes.ok) {
        const damageTableError = damageTableRes ? await damageTableRes.json() : { error: "No response" };
        throw new Error(`Damage Table upload failed: ${damageTableError.error || damageTableRes?.statusText || "Unknown error"}`);
      }

      if (!unitTraitEffectsRes || !unitTraitEffectsRes.ok) {
        const unitTraitEffectsError = unitTraitEffectsRes ? await unitTraitEffectsRes.json() : { error: "No response" };
        throw new Error(`Unit Trait Effects upload failed: ${unitTraitEffectsError.error || unitTraitEffectsRes?.statusText || "Unknown error"}`);
      }

      if (!terrainSpeedModifiersRes || !terrainSpeedModifiersRes.ok) {
        const terrainSpeedModifiersError = terrainSpeedModifiersRes ? await terrainSpeedModifiersRes.json() : { error: "No response" };
        throw new Error(`Terrain Speed Modifiers upload failed: ${terrainSpeedModifiersError.error || terrainSpeedModifiersRes?.statusText || "Unknown error"}`);
      }

      const unitsResult = await unitsRes.json();
      const weaponsResult = await weaponsRes.json();
      const divisionsResult = await divisionsRes.json();
      const damageTableResult = await damageTableRes.json();
      const unitTraitEffectsResult = await unitTraitEffectsRes.json();
      const terrainSpeedModifiersResult = await terrainSpeedModifiersRes.json();

      const successParts = [
        `Units: ${unitsResult.is_new ? "created" : "updated"}`,
        `Weapons: ${weaponsResult.is_new ? "created" : "updated"}`,
        `Divisions: ${divisionsResult.is_new ? "created" : "updated"}`,
        `Damage Table: ${damageTableResult.is_new ? "created" : "updated"}`,
        `Unit Trait Effects: ${unitTraitEffectsResult.is_new ? "created" : "updated"}`,
        `Terrain Speed Modifiers: ${terrainSpeedModifiersResult.is_new ? "created" : "updated"}`,
      ];

      setSuccess(`Successfully uploaded version ${version}! ${successParts.join(", ")}`);

      // Reset form
      setUnitsFile(null);
      setWeaponsFile(null);
      setDivisionsFile(null);
      setDamageTableFile(null);
      setUnitTraitEffectsFile(null);
      setTerrainSpeedModifiersFile(null);
      setVersion("");

      // Reset file inputs
      const unitsInput = document.getElementById("units-file") as HTMLInputElement;
      const weaponsInput = document.getElementById("weapons-file") as HTMLInputElement;
      const divisionsInput = document.getElementById("divisions-file") as HTMLInputElement;
      const damageTableInput = document.getElementById("damage-table-file") as HTMLInputElement;
      const unitTraitEffectsInput = document.getElementById("unit-trait-effects-file") as HTMLInputElement;
      const terrainSpeedModifiersInput = document.getElementById("terrain-speed-modifiers-file") as HTMLInputElement;
      if (unitsInput) unitsInput.value = "";
      if (weaponsInput) weaponsInput.value = "";
      if (divisionsInput) divisionsInput.value = "";
      if (damageTableInput) damageTableInput.value = "";
      if (unitTraitEffectsInput) unitTraitEffectsInput.value = "";
      if (terrainSpeedModifiersInput) terrainSpeedModifiersInput.value = "";
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError("Invalid JSON file. Please check your files are valid JSON.");
      } else {
        setError(err instanceof Error ? err.message : "Upload failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(3)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(3)} MB`;
  };

  // File matching map: filename pattern -> setter function
  const fileMatchers: Array<{
    pattern: RegExp;
    setter: (file: File) => void;
    name: string;
  }> = [
    { pattern: /^units\.json$/i, setter: setUnitsFile, name: "units" },
    { pattern: /^weapons\.json$/i, setter: setWeaponsFile, name: "weapons" },
    { pattern: /^divisions\.json$/i, setter: setDivisionsFile, name: "divisions" },
    { pattern: /^damage-table\.json$/i, setter: setDamageTableFile, name: "damage-table" },
    { pattern: /^unit-trait-effects\.json$/i, setter: setUnitTraitEffectsFile, name: "unit-trait-effects" },
    { pattern: /^terrain-speed-modifiers\.json$/i, setter: setTerrainSpeedModifiersFile, name: "terrain-speed-modifiers" },
  ];

  const handleMultiFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const matchedFiles: string[] = [];
    const unmatchedFiles: string[] = [];

    // Process each selected file
    Array.from(files).forEach((file) => {
      // Extract just the filename (no path)
      const fileName = file.name.split(/[/\\]/).pop() || file.name;
      let matched = false;

      // Try to match against each pattern
      for (const matcher of fileMatchers) {
        if (matcher.pattern.test(fileName)) {
          matcher.setter(file);
          matchedFiles.push(`${fileName} → ${matcher.name}`);
          matched = true;
          break;
        }
      }

      if (!matched) {
        unmatchedFiles.push(fileName);
      }
    });

    // Show feedback
    if (matchedFiles.length > 0) {
      const feedback = [
        `Matched ${matchedFiles.length} file(s):`,
        ...matchedFiles.map((m) => `  • ${m}`),
      ];
      if (unmatchedFiles.length > 0) {
        feedback.push(`\nUnmatched ${unmatchedFiles.length} file(s):`, ...unmatchedFiles.map((f) => `  • ${f}`));
      }
      // Use a separate feedback state for multi-file selection
      setMultiFileFeedback(feedback.join("\n"));
      setTimeout(() => setMultiFileFeedback(null), 5000);
    }

    if (unmatchedFiles.length > 0 && matchedFiles.length === 0) {
      setError(`Could not auto-match files. Please ensure filenames match expected patterns (e.g., units.json, weapons.json, etc.)`);
      setTimeout(() => setError(null), 5000);
    }

    // Reset the input so the same files can be selected again if needed
    e.target.value = "";
  };

  const triggerMultiFileSelect = () => {
    const input = document.getElementById("multi-file-select") as HTMLInputElement;
    input?.click();
  };

  if (checkingAuth) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Card>
    );
  }

  if (!user) {
    return (
      <Card className="p-6">
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Lock className="h-12 w-12 text-muted-foreground" />
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">Authentication Required</h2>
            <p className="text-muted-foreground mb-6">
              Please log in to upload new versions
            </p>
            <LoginButton />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <Upload className="h-5 w-5" />
        <h2 className="text-2xl font-bold">Upload New Version</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Multi-file selector */}
        <div className="space-y-2">
          <Label>Quick Upload</Label>
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={triggerMultiFileSelect}
              className="cursor-pointer"
            >
              <Upload className="h-4 w-4 mr-2" />
              Select All Files
            </Button>
            <p className="text-xs text-muted-foreground">
              Select multiple files at once - they will be auto-matched by filename
            </p>
          </div>
          <Input
            id="multi-file-select"
            type="file"
            accept=".json"
            multiple
            onChange={handleMultiFileSelect}
            className="hidden"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="version">Version Number *</Label>
          <Input
            id="version"
            type="text"
            placeholder="v1.0.0"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            pattern="^v\d+\.\d+\.\d+$"
            required
          />
          <p className="text-xs text-muted-foreground">
            Format: v1.0.0 (semantic versioning)
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="units-file">Units JSON File *</Label>
          <div className="flex items-center gap-4">
            <Input
              id="units-file"
              type="file"
              accept=".json"
              onChange={(e) => setUnitsFile(e.target.files?.[0] || null)}
              className="cursor-pointer"
            />
            {unitsFile && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileJson className="h-4 w-4" />
                <span>{unitsFile.name}</span>
                <span>({formatFileSize(unitsFile.size)})</span>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="weapons-file">Weapons JSON File *</Label>
          <div className="flex items-center gap-4">
            <Input
              id="weapons-file"
              type="file"
              accept=".json"
              onChange={(e) => setWeaponsFile(e.target.files?.[0] || null)}
              className="cursor-pointer"
            />
            {weaponsFile && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileJson className="h-4 w-4" />
                <span>{weaponsFile.name}</span>
                <span>({formatFileSize(weaponsFile.size)})</span>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="divisions-file">Divisions JSON File *</Label>
          <div className="flex items-center gap-4">
            <Input
              id="divisions-file"
              type="file"
              accept=".json"
              onChange={(e) => setDivisionsFile(e.target.files?.[0] || null)}
              className="cursor-pointer"
            />
            {divisionsFile && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileJson className="h-4 w-4" />
                <span>{divisionsFile.name}</span>
                <span>({formatFileSize(divisionsFile.size)})</span>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="damage-table-file">Damage Table JSON File *</Label>
          <div className="flex items-center gap-4">
            <Input
              id="damage-table-file"
              type="file"
              accept=".json"
              onChange={(e) => setDamageTableFile(e.target.files?.[0] || null)}
              className="cursor-pointer"
            />
            {damageTableFile && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileJson className="h-4 w-4" />
                <span>{damageTableFile.name}</span>
                <span>({formatFileSize(damageTableFile.size)})</span>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="unit-trait-effects-file">Unit Trait Effects JSON File *</Label>
          <div className="flex items-center gap-4">
            <Input
              id="unit-trait-effects-file"
              type="file"
              accept=".json"
              onChange={(e) => setUnitTraitEffectsFile(e.target.files?.[0] || null)}
              className="cursor-pointer"
            />
            {unitTraitEffectsFile && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileJson className="h-4 w-4" />
                <span>{unitTraitEffectsFile.name}</span>
                <span>({formatFileSize(unitTraitEffectsFile.size)})</span>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="terrain-speed-modifiers-file">Terrain Speed Modifiers JSON File *</Label>
          <div className="flex items-center gap-4">
            <Input
              id="terrain-speed-modifiers-file"
              type="file"
              accept=".json"
              onChange={(e) => setTerrainSpeedModifiersFile(e.target.files?.[0] || null)}
              className="cursor-pointer"
            />
            {terrainSpeedModifiersFile && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileJson className="h-4 w-4" />
                <span>{terrainSpeedModifiersFile.name}</span>
                <span>({formatFileSize(terrainSpeedModifiersFile.size)})</span>
              </div>
            )}
          </div>
        </div>

        {multiFileFeedback && (
          <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500 rounded-md text-blue-600 dark:text-blue-400">
            <CheckCircle2 className="h-4 w-4 mt-0.5" />
            <pre className="text-xs whitespace-pre-wrap font-mono">{multiFileFeedback}</pre>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive rounded-md text-destructive">
            <XCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500 rounded-md text-green-600 dark:text-green-400">
            <CheckCircle2 className="h-4 w-4" />
            <span>{success}</span>
          </div>
        )}

        <Button type="submit" disabled={loading} className="w-full">
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Upload Version
            </>
          )}
        </Button>
      </form>
    </Card>
  );
}

